import { useEffect, useState } from 'react';
import styled from 'styled-components';
import OptionModal from '../filterOptionModal/OptionModal';
import RegionOption from '../filterOptionModal/RegionOption';
import { BidStatusList, FilterType } from '../FilterData';
import TotalCategoryOption from '../filterOptionModal/TotalCategoryOption';
import CourtOption from '../filterOptionModal/CourtOption';
import ProgressStatusOption from '../filterOptionModal/ProgressStatusOption';
import DateOption from '../filterOptionModal/DateOption';
import BidnoOption from '../filterOptionModal/BidnoOption';
import UseAreaOption from '../filterOptionModal/UseAreaOption';
import SpecialConditionOption from '../filterOptionModal/SpecialConditionOption';
import { useSearchParams } from 'react-router-dom';

interface Props {
  filters: FilterType;
  setFilters: (filters: FilterType) => void;
  moreOptionCount: number;
  isMain?: boolean;
  handleMoreOpen?: () => void;
  hasSearchButton?: boolean;
  handleReset?: () => void;
  handleSearch?: () => void;
}

function SearchFilter({
  filters,
  setFilters,
  moreOptionCount,
  isMain,
  handleMoreOpen,
  hasSearchButton = false,
  handleReset,
  handleSearch,
}: Props) {
  const [searchParams] = useSearchParams();
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  useEffect(() => {
    const searchType = searchParams.get('type');
    setIsMoreOpen(searchType === 'c');
  }, [searchParams]);
  const [openOption, setOpenOption] = useState<
    | null
    | 'region'
    | 'totalCategory'
    | 'court'
    | 'progressStatus'
    | 'saleDateStart'
    | 'saleDateEnd'
    | 'approvalDateStart'
    | 'approvalDateEnd'
    | 'bidno'
  >(null);
  const [openUseAreaOption, setOpenUseAreaOption] = useState(false);
  const [openSpecitalConditionOption, setOpenSpecitalConditionOption] =
    useState(false);

  const [isMinAppraiseFocus, setIsMinAppraiseFocus] = useState(false);
  const [isMaxAppraiseFocus, setIsMaxAppraiseFocus] = useState(false);
  const [isMinPriceMinFocus, setIsMinPriceMinFocus] = useState(false);
  const [isMinPriceMaxFocus, setIsMinPriceMaxFocus] = useState(false);
  const [buildingAreaMinInput, setBuildingAreaMinInput] = useState('');
  const [buildingAreaMaxInput, setBuildingAreaMaxInput] = useState('');
  const [landAreaMinInput, setLandAreaMinInput] = useState('');
  const [landAreaMaxInput, setLandAreaMaxInput] = useState('');

  const [buildingAreaUnit, setBuildingAreaUnit] = useState<'m2' | 'pyeong'>(
    'm2',
  );
  const [landAreaUnit, setLandAreaUnit] = useState<'m2' | 'pyeong'>('m2');

  const foramtDateWithDot = (value: string) => {
    return `${value.replaceAll('-', '.')}.`;
  };

  const handlePriceChange = (
    e: any,
    category: 'appraisalPrice' | 'minPrice',
    type: 'min' | 'max',
  ) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setFilters({
      ...filters,
      [category]: {
        ...filters[category],
        [type]: value,
      },
    });
  };

  const togglePyeongM2Value = (value: string, unit: string) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return unit === 'pyeong'
      ? (num * 3.30579).toFixed(2)
      : (num / 3.30579).toFixed(2);
  };

  const handleAreaChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    category: 'buildingArea' | 'landArea',
    type: 'min' | 'max',
    setInput: (input: string) => void,
  ) => {
    setInput(e.target.value);
    const rawValue = e.target.value.replace(/[^0-9.]/g, '');
    const unit = category === 'buildingArea' ? buildingAreaUnit : landAreaUnit;

    const m2 =
      unit === 'pyeong'
        ? (parseFloat(rawValue) * 3.30579).toString()
        : rawValue;

    setFilters({
      ...filters,
      [category]: {
        ...filters[category],
        [type]: m2,
      },
    });
  };

  const toggleUnit = (
    unit: 'm2' | 'pyeong',
    setUnit: (unit: 'm2' | 'pyeong') => void,
    minInput: string,
    setMinInput: (input: string) => void,
    maxInput: string,
    setMaxInput: (input: string) => void,
  ) => {
    const toUnit = unit === 'm2' ? 'pyeong' : 'm2';
    setUnit(toUnit);

    if (minInput !== '') {
      setMinInput(togglePyeongM2Value(minInput, unit));
    }

    if (maxInput !== '') {
      setMaxInput(togglePyeongM2Value(maxInput, unit));
    }
  };

  const closeModal = () => {
    setOpenOption(null);
  };

  const renderModal = () => {
    switch (openOption) {
      case 'region':
        return (
          <RegionOption
            onClose={closeModal}
            filters={filters}
            setFilters={setFilters}
          />
        );
      case 'court':
        return (
          <CourtOption
            onClose={closeModal}
            filters={filters}
            setFilters={setFilters}
          />
        );
      case 'totalCategory':
        return (
          <TotalCategoryOption
            onClose={closeModal}
            filters={filters}
            setFilters={setFilters}
          />
        );
      case 'progressStatus':
        return (
          <ProgressStatusOption
            onClose={closeModal}
            filters={filters}
            setFilters={setFilters}
          />
        );
      case 'saleDateStart':
        return (
          <DateOption
            option="saleDate"
            option2="start"
            onClose={closeModal}
            filters={filters}
            setFilters={setFilters}
          />
        );
      case 'saleDateEnd':
        return (
          <DateOption
            option="saleDate"
            option2="end"
            onClose={closeModal}
            filters={filters}
            setFilters={setFilters}
          />
        );
      case 'approvalDateStart':
        return (
          <DateOption
            option="approvalDate"
            option2="start"
            onClose={closeModal}
            filters={filters}
            setFilters={setFilters}
          />
        );
      case 'approvalDateEnd':
        return (
          <DateOption
            option="approvalDate"
            option2="end"
            onClose={closeModal}
            filters={filters}
            setFilters={setFilters}
          />
        );
      case 'bidno':
        return (
          <BidnoOption
            onClose={closeModal}
            filters={filters}
            setFilters={setFilters}
          />
        );
    }
  };

  return (
    <FilterInner>
      <OptionRowBox>
        <TabButtonBox>
          <TabButton
            isActive={filters.type === 'a'}
            onClick={() => {
              setFilters({ ...filters, type: 'a' });
            }}
          >
            경매
          </TabButton>
          <TabButton
            isActive={filters.type === 'p'}
            onClick={() => {
              setFilters({ ...filters, type: 'p', regionOrCourt: 'r' });
            }}
          >
            공매
          </TabButton>
        </TabButtonBox>
        <TabButtonBox>
          <TabButton
            isActive={filters.regionOrCourt === 'r'}
            disabled={filters.type === 'p'}
            onClick={() => {
              setFilters({ ...filters, regionOrCourt: 'r', court: '' });
            }}
            style={{
              border:
                filters.type === 'p' ? '1px solid rgba(55, 56, 60, 0.16)' : '',
              color: filters.type === 'p' ? 'rgba(55, 56, 60, 0.16)' : '',
            }}
          >
            지역
          </TabButton>
          <TabButton
            isActive={filters.regionOrCourt === 'c'}
            disabled={filters.type === 'p'}
            onClick={() => {
              setFilters({ ...filters, regionOrCourt: 'c', region: [] });
            }}
            style={{
              color: filters.type === 'p' ? 'rgba(55, 56, 60, 0.16)' : '',
            }}
          >
            법원
          </TabButton>
        </TabButtonBox>
      </OptionRowBox>

      {filters.regionOrCourt === 'r' ? (
        <OptionButton
          isNull={filters.region.length === 0 && filters.address === ''}
          onClick={() => setOpenOption('region')}
        >
          <LocationOption>
            <img src="/assets/images/icon/location.svg" alt="" />
            {filters.address !== ''
              ? filters.address
              : filters.region.length === 0 ||
                  filters.region.every(
                    (item) => !item.sido && !item.sigungu && !item.emd,
                  )
                ? '지역'
                : filters.region
                    .map((item) => `${item.sido} ${item.sigungu} ${item.emd}`)
                    .join(',')}
          </LocationOption>
          <img src="/assets/images/arrow/black_arrow_down_20.svg" alt="" />
        </OptionButton>
      ) : (
        <OptionButton
          isNull={filters.court === ''}
          onClick={() => setOpenOption('court')}
        >
          {filters.court === '' ? '법원' : filters.court}
          <img src="/assets/images/arrow/black_arrow_down_20.svg" alt="" />
        </OptionButton>
      )}

      {filters.type === 'p' ? (
        <OptionRowBox>
          <NumberInput
            type="string"
            pattern="[0-9]*"
            placeholder="물건번호"
            value={filters.caseNumber || ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                caseNumber: e.target.value,
              })
            }
          />
        </OptionRowBox>
      ) : (
        <OptionRowBox gap="8px">
          <NumberInput
            type="number"
            pattern="[0-9]*"
            placeholder="연도"
            value={filters.year || ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= 4) {
                setFilters({
                  ...filters,
                  year: value,
                });
              }
            }}
            style={{ width: '100px' }}
          />
          <Text>타경</Text>
          <NumberInput
            type="number"
            pattern="[0-9]*"
            placeholder="사건번호"
            value={filters.caseNumber || ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                caseNumber: e.target.value,
              })
            }
          />
        </OptionRowBox>
      )}

      <OptionRowBox>
        <OptionButton
          isNull={filters.itemStatus.length === 0}
          onClick={() => setOpenOption('totalCategory')}
        >
          {filters.itemStatus.length === 0
            ? '물건종류'
            : filters.itemStatus.length > 1
              ? `${filters.itemStatus[0]} 외 +${filters.itemStatus.length - 1}`
              : `${filters.itemStatus[0]}`}
          <img src="/assets/images/arrow/black_arrow_down_20.svg" alt="" />
        </OptionButton>
        <OptionButton
          isNull={filters.progressStatus === ''}
          onClick={() => setOpenOption('progressStatus')}
        >
          {filters.progressStatus === '' ? '진행단계' : filters.progressStatus}
          <img src="/assets/images/arrow/black_arrow_down_20.svg" alt="" />
        </OptionButton>
      </OptionRowBox>

      {isMoreOpen && (
        <MoreSection>
          <Option>
            <OptionTitle>감정가격</OptionTitle>
            <OptionRowBox gap="4px">
              <UnitInputWrapper>
                <NumberInput
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  style={{ padding: 0, borderRadius: 0 }}
                  placeholder="최소"
                  value={
                    isMinAppraiseFocus
                      ? filters.appraisalPrice.min
                      : filters.appraisalPrice.min === ''
                        ? ''
                        : Number(filters.appraisalPrice.min).toLocaleString()
                  }
                  onFocus={() => setIsMinAppraiseFocus(true)}
                  onBlur={() => setIsMinAppraiseFocus(false)}
                  onChange={(e) =>
                    handlePriceChange(e, 'appraisalPrice', 'min')
                  }
                />
                <span style={{ whiteSpace: 'nowrap' }}>만원</span>
              </UnitInputWrapper>
              <Text>~</Text>
              <UnitInputWrapper>
                <NumberInput
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  style={{ padding: 0, borderRadius: 0 }}
                  placeholder="최대"
                  value={
                    isMaxAppraiseFocus
                      ? filters.appraisalPrice.max
                      : filters.appraisalPrice.max === ''
                        ? ''
                        : Number(filters.appraisalPrice.max).toLocaleString()
                  }
                  onFocus={() => setIsMaxAppraiseFocus(true)}
                  onBlur={() => setIsMaxAppraiseFocus(false)}
                  onChange={(e) =>
                    handlePriceChange(e, 'appraisalPrice', 'max')
                  }
                />
                <span style={{ whiteSpace: 'nowrap' }}>만원</span>
              </UnitInputWrapper>
            </OptionRowBox>
          </Option>

          <Option>
            <OptionTitle>최저가격</OptionTitle>
            <OptionRowBox gap="4px">
              <UnitInputWrapper>
                <NumberInput
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  style={{ padding: 0, borderRadius: 0 }}
                  placeholder="최소"
                  value={
                    isMinPriceMinFocus
                      ? filters.minPrice.min
                      : filters.minPrice.min === ''
                        ? ''
                        : Number(filters.minPrice.min).toLocaleString()
                  }
                  onFocus={() => setIsMinPriceMinFocus(true)}
                  onBlur={() => setIsMinPriceMinFocus(false)}
                  onChange={(e) => handlePriceChange(e, 'minPrice', 'min')}
                />
                <span style={{ whiteSpace: 'nowrap' }}>만원</span>
              </UnitInputWrapper>
              <Text>~</Text>
              <UnitInputWrapper>
                <NumberInput
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  style={{ padding: 0, borderRadius: 0 }}
                  placeholder="최대"
                  value={
                    isMinPriceMaxFocus
                      ? filters.minPrice.max
                      : filters.minPrice.max === ''
                        ? ''
                        : Number(filters.minPrice.max).toLocaleString()
                  }
                  onFocus={() => setIsMinPriceMaxFocus(true)}
                  onBlur={() => setIsMinPriceMaxFocus(false)}
                  onChange={(e) => handlePriceChange(e, 'minPrice', 'max')}
                />
                <span style={{ whiteSpace: 'nowrap' }}>만원</span>
              </UnitInputWrapper>
            </OptionRowBox>
          </Option>

          <Option>
            <OptionTitle>매각기일</OptionTitle>
            <OptionRowBox gap="4px">
              <DateInput
                isNull={filters.saleDate.start === ''}
                onClick={() => setOpenOption('saleDateStart')}
              >
                {filters.saleDate.start === ''
                  ? '연도. 월. 일.'
                  : foramtDateWithDot(filters.saleDate.start)}
              </DateInput>
              <Text>~</Text>
              <DateInput
                isNull={filters.saleDate.end === ''}
                onClick={() => setOpenOption('saleDateEnd')}
              >
                {filters.saleDate.end === ''
                  ? '연도. 월. 일.'
                  : foramtDateWithDot(filters.saleDate.end)}
              </DateInput>
            </OptionRowBox>
          </Option>

          <Option>
            <OptionTitle>사용승인</OptionTitle>
            <OptionRowBox gap="4px">
              <DateInput
                isNull={filters.approvalDate.start === ''}
                onClick={() => setOpenOption('approvalDateStart')}
              >
                {filters.approvalDate.start === ''
                  ? '연도. 월. 일.'
                  : foramtDateWithDot(filters.approvalDate.start)}
              </DateInput>
              <Text>~</Text>
              <DateInput
                isNull={filters.approvalDate.end === ''}
                onClick={() => setOpenOption('approvalDateEnd')}
              >
                {filters.approvalDate.end === ''
                  ? '연도. 월. 일.'
                  : foramtDateWithDot(filters.approvalDate.end)}
              </DateInput>
            </OptionRowBox>
          </Option>

          <Option>
            <OptionTitle>
              건물면적
              <ChangeUnitButton
                onClick={() =>
                  toggleUnit(
                    buildingAreaUnit,
                    setBuildingAreaUnit,
                    buildingAreaMinInput,
                    setBuildingAreaMinInput,
                    buildingAreaMaxInput,
                    setBuildingAreaMaxInput,
                  )
                }
              >
                {buildingAreaUnit === 'm2' ? 'm²' : '평'}
              </ChangeUnitButton>
            </OptionTitle>
            <OptionRowBox gap="4px">
              <UnitInputWrapper>
                <NumberInput
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9.]*"
                  style={{ padding: 0, borderRadius: 0 }}
                  value={buildingAreaMinInput}
                  onChange={(e) =>
                    handleAreaChange(
                      e,
                      'buildingArea',
                      'min',
                      setBuildingAreaMinInput,
                    )
                  }
                />
                <span style={{ whiteSpace: 'nowrap' }}>
                  {buildingAreaUnit === 'm2' ? 'm²' : '평'}
                </span>
              </UnitInputWrapper>
              <Text>~</Text>
              <UnitInputWrapper>
                <NumberInput
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9.]*"
                  style={{ padding: 0, borderRadius: 0 }}
                  value={buildingAreaMaxInput}
                  onChange={(e) =>
                    handleAreaChange(
                      e,
                      'buildingArea',
                      'max',
                      setBuildingAreaMaxInput,
                    )
                  }
                />
                <span style={{ whiteSpace: 'nowrap' }}>
                  {buildingAreaUnit === 'm2' ? 'm²' : '평'}
                </span>
              </UnitInputWrapper>
            </OptionRowBox>
          </Option>

          <Option>
            <OptionTitle>
              토지면적
              <ChangeUnitButton
                onClick={() =>
                  toggleUnit(
                    landAreaUnit,
                    setLandAreaUnit,
                    landAreaMinInput,
                    setLandAreaMinInput,
                    landAreaMaxInput,
                    setLandAreaMaxInput,
                  )
                }
              >
                {landAreaUnit === 'm2' ? 'm²' : '평'}
              </ChangeUnitButton>
            </OptionTitle>
            <OptionRowBox gap="4px">
              <UnitInputWrapper>
                <NumberInput
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9.]*"
                  style={{ padding: 0, borderRadius: 0 }}
                  value={landAreaMinInput}
                  onChange={(e) =>
                    handleAreaChange(e, 'landArea', 'min', setLandAreaMinInput)
                  }
                />
                <span style={{ whiteSpace: 'nowrap' }}>
                  {landAreaUnit === 'm2' ? 'm²' : '평'}
                </span>
              </UnitInputWrapper>
              <Text>~</Text>
              <UnitInputWrapper>
                <NumberInput
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9.]*"
                  style={{ padding: 0, borderRadius: 0 }}
                  value={landAreaMaxInput}
                  onChange={(e) =>
                    handleAreaChange(e, 'landArea', 'max', setLandAreaMaxInput)
                  }
                />
                <span style={{ whiteSpace: 'nowrap' }}>
                  {landAreaUnit === 'm2' ? 'm²' : '평'}
                </span>
              </UnitInputWrapper>
            </OptionRowBox>
          </Option>

          <OptionRowBox>
            <Option>
              <OptionTitle>유찰횟수</OptionTitle>
              <OptionButton
                isNull={filters.bidno === ''}
                onClick={() => setOpenOption('bidno')}
              >
                {filters.bidno === ''
                  ? '선택'
                  : BidStatusList.find((item) => item.value === filters.bidno)
                      ?.label}
              </OptionButton>
            </Option>
            <Option>
              <OptionTitle>용도지역</OptionTitle>
              <OptionButton
                isNull={filters.useArea.length === 0}
                onClick={() => setOpenUseAreaOption(true)}
              >
                {filters.useArea.length === 0
                  ? '선택'
                  : filters.useArea.length > 1
                    ? `${filters.useArea[0]} 외 +${filters.useArea.length - 1}`
                    : `${filters.useArea[0]}`}
              </OptionButton>
            </Option>
          </OptionRowBox>

          <Option>
            <OptionTitle>특수조건</OptionTitle>
            <OptionButton
              isNull={filters.specialItemCondition.length === 0}
              onClick={() => setOpenSpecitalConditionOption(true)}
            >
              {filters.specialItemCondition.length === 0
                ? '선택'
                : filters.specialItemCondition.length > 1
                  ? `${filters.specialItemCondition[0]} 외 +${filters.specialItemCondition.length - 1}`
                  : `${filters.specialItemCondition[0]}`}
            </OptionButton>
          </Option>
        </MoreSection>
      )}

      <MoreButton
        onClick={() => {
          if (isMain && handleMoreOpen) {
            handleMoreOpen();
          } else {
            setIsMoreOpen((prev) => !prev);
          }
        }}
      >
        검색조건 {isMoreOpen ? '접기' : '더보기'}
        {isMoreOpen ? (
          <img
            src="/assets/images/arrow/ArrowTop24-light-gray.svg"
            alt=""
            width={16}
            height={16}
          />
        ) : (
          <img
            src="/assets/images/arrow/arrow-down-gray.svg"
            alt=""
            width={16}
            height={16}
          />
        )}
        {!isMoreOpen && moreOptionCount > 0 && (
          <MoreCountBadge>{moreOptionCount}</MoreCountBadge>
        )}
      </MoreButton>

      {openOption !== null && (
        <OptionModal option={openOption} onClose={closeModal}>
          {renderModal()}
        </OptionModal>
      )}
      {openUseAreaOption && (
        <UseAreaOption
          onClose={() => setOpenUseAreaOption(false)}
          filters={filters}
          setFilters={setFilters}
        />
      )}
      {openSpecitalConditionOption && (
        <SpecialConditionOption
          onClose={() => setOpenSpecitalConditionOption(false)}
          filters={filters}
          setFilters={setFilters}
        />
      )}

      {hasSearchButton && (
        <ButtonContainer>
          <ResetButton onClick={handleReset}>
            <img src="/assets/images/icon/reset.svg" alt="" />
            <span>초기화</span>
          </ResetButton>
          <ApplyButton onClick={handleSearch}>검색</ApplyButton>
        </ButtonContainer>
      )}
    </FilterInner>
  );
}

export default SearchFilter;

const FilterInner = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 16px;
  background-color: #fff;
  box-shadow: 0px 4px 24px 0px #3d454e1a;
`;

/**
 * @param {string} gap 기본 16px
 */
const OptionRowBox = styled.div<{ gap?: string }>`
  display: flex;
  flex-direction: row;
  gap: ${({ gap }) => (gap ? gap : '16px')};
`;

const Option = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const OptionTitle = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #222222;
`;

const TabButtonBox = styled.div`
  width: 100%;
  padding: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background-color: #f7f8fc;
`;

const TabButton = styled.button<{ isActive: boolean }>`
  flex: 1;
  padding: 8px 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: ${({ isActive }) => (isActive ? '1px solid #008AFF' : 'none')};
  background-color: ${({ isActive }) => (isActive ? '#fff' : 'transparent')};
  box-shadow: ${({ isActive }) =>
    isActive &&
    '0px 2px 8px 0px rgba(56, 58, 59, 0.12), 0px 1px 4px 0px rgba(56, 58, 59, 0.08), 0px 0px 1px 0px rgba(56, 58, 59, 0.08)'};
  font-size: 14px;
  font-weight: 500;
  color: ${({ isActive }) => (isActive ? '#008AFF' : '#989BA2')};
  cursor: pointer;
`;

const Text = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #616161;
  white-space: nowrap;
  display: flex;
  align-items: center;
`;

const NumberInput = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  background-color: #f7f8fc;
  border: none;

  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;

  &::placeholder {
    color: #616161;
    font-weight: 500;
    font-size: 14px;
    line-height: 145%;
    letter-spacing: -2%;
  }
  -webkit-appearance: none;
  appearance: none;

  &:focus {
    outline: none;
    border-color: rgba(23, 30, 38, 0.3);
  }
`;

const OptionButton = styled.button<{ isNull?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  border-radius: 8px;
  padding: 12px;
  background-color: #f7f8fc;
  justify-content: space-between;
  color: ${({ isNull }) => (isNull ? '#616161' : '#222222')};
  border: none;
  white-space: nowrap;

  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
`;

const LocationOption = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;

const MoreButton = styled.button`
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 4px;
  justify-content: center;
  background-color: #fff;
  align-items: center;
  border-radius: 4px;
  border: 1px solid #e1e2e4;
  padding: 12px 16px;

  font-weight: 500;
  font-size: 14px;
  color: #616161;
`;

const MoreCountBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -6px;

  border-radius: 16px;
  padding: 2px 4px;
  background-color: #222222;

  font-weight: 700;
  font-size: 12px;
  line-height: 145%;
  letter-spacing: -2%;
  text-align: center;
  color: #ffffff;
`;

const MoreSection = styled.div`
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const UnitInputWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  background-color: #f7f8fc;
  color: #616161;
  font-size: 14px;
  font-weight: 500;
`;

const DateInput = styled.div<{ isNull?: boolean }>`
  width: 100%;
  border-radius: 8px;
  padding: 10px 12px;
  background-color: #f7f8fc;

  font-family: Spoqa Han Sans Neo;
  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  color: ${({ isNull }) => (isNull ? '#616161' : '#222222')};
`;

const ChangeUnitButton = styled.button`
  border-radius: 4px;
  padding: 2px 6px;
  border: 1px solid #70737c38;
  font-family: Pretendard;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  color: #222;
  background-color: #fff;
`;

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: flex-start;
  align-content: flex-start;
  gap: 24px 8px;
  align-self: stretch;
  flex-wrap: wrap;
`;

const ResetButton = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 1px solid #e1e2e4;
  background-color: #fff;
  font-size: 12px;
  font-weight: 400;
  color: #616161;
  cursor: pointer;
  padding: 7px 14px;
`;
const ApplyButton = styled.button`
  flex: 1;
  border-radius: 8px;
  border: none;
  padding: 12px 28px;
  background-color: #008aff;
  font-size: 16px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
`;
